import React, { useMemo } from "react";

import { Root } from "@radix-ui/react-accordion";

import { useConnection } from "src/api/connectApi/queries";
import { Connection, ProviderResource } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { FieldErrors } from "src/api/types";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { LoadingView } from "src/base-components/LoadingView";
import { checkIfFileIsEmpty } from "src/baseConnectionNode/utils";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import {
  CONNECTIONS_MAP,
  PROVIDER_RESOURCES_WITH_FILE_EXTRACTORS,
} from "src/connections/connectionConfigs";
import { ResourceConfig } from "src/connections/connectionConfigs/types";
import {
  IntegrationNode,
  IntegrationNodeData,
} from "src/constants/NodeDataTypes";
import { ConfigPane } from "src/integrationNode/editorComponents/ConfigPane";
import { InputMappings } from "src/integrationNode/editorComponents/InputMappings";
import {
  FileOutputMappings,
  OutputMappings,
} from "src/integrationNode/editorComponents/OutputMappings";
import {
  Config,
  InputMappings as InputMappingsType,
  OutputMapping,
  OutputMappings as OutputMappingsType,
} from "src/integrationNode/types";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";

type Props = {
  node: IntegrationNode;
  connection: Connection;
} & NodeEditorBaseProps<IntegrationNodeData>;

type WrapperProps = {
  node: IntegrationNode;
  workspaceId: string;
} & NodeEditorBaseProps<IntegrationNodeData>;

export type SubFormBaseProps = Pick<
  NodeEditorBaseProps<IntegrationNodeData>,
  "immutable" | "isReactive"
>;

export const isFileHandlingProviderResource = (
  providerResource: ProviderResource,
): boolean => {
  const fileExtractorResources =
    PROVIDER_RESOURCES_WITH_FILE_EXTRACTORS[
      providerResource.provider as keyof typeof PROVIDER_RESOURCES_WITH_FILE_EXTRACTORS
    ];
  return fileExtractorResources?.includes(providerResource.resource) ?? false;
};

export const getDefaultFiles = (providerResource: ProviderResource) => {
  const connection =
    CONNECTIONS_MAP[providerResource.provider as keyof typeof CONNECTIONS_MAP];
  if (!connection) return undefined;
  const resourceConfig: ResourceConfig =
    connection.resources[
      providerResource.resource as keyof typeof connection.resources
    ];
  if (!resourceConfig) return undefined;
  const files = resourceConfig.integrationResource().files;
  return files || {};
};

export const IntegrationNodeEditor: React.FC<WrapperProps> = ({
  immutable,
  node,
  workspaceId,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const workspace = useWorkspace(workspaceId);
  const connection = useConnection({
    baseUrl: workspace.data?.base_url,
    id: node.data.connectionId,
  });

  return (
    <LoadingView
      queryResult={connection}
      renderUpdated={(connection: Connection) => (
        <IntegrationNodeEditorInternal
          connection={connection}
          displayedError={displayedError}
          immutable={immutable}
          isReactive={isReactive}
          node={node}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

const IntegrationNodeEditorInternal: React.FC<Props> = ({
  immutable,
  node,
  connection,
  displayedError,
  isReactive,
  onUpdate,
}) => {
  const { renderedInDiffView } = useDiffViewContext();
  const updateMappingInput = (inputMapping: InputMappingsType) => {
    onUpdate({ newData: { input: inputMapping } });
  };

  const updateMappingOutput = (outputMapping: OutputMappingsType) => {
    onUpdate({
      newData: { output: outputMapping },
    });
  };

  const updateMappingFiles = (filesMapping: {
    [key: string]: OutputMapping;
  }) => {
    onUpdate({
      newData: { files: filesMapping },
    });
  };

  const updateConfig = (updatedConfig: Config) => {
    // This check gives a Typescript error when the config property is not present on integration nodes.
    if (node.data.config)
      onUpdate({
        newData: { config: updatedConfig },
      });
  };

  const multiTenantConnection = Boolean(connection.configuration.multi_tenant);
  const hasInputMappings = Object.values(node.data.input)
    .map((partialInputs) => Object.keys(partialInputs).length)
    .some((partialInputsLength) => partialInputsLength > 0);

  const runFieldErrors: FieldErrors | undefined = displayedError?.field_errors
    ? convertFieldErrorsBeToFe(displayedError.field_errors)
    : undefined;

  const hasRawProviderRequestEnabled = connection.resource_configs.find(
    (resource) => resource.id === node.data.resourceConfigId,
  )?.has_raw_requests_enabled_in_resource;

  const defaultOpenSections = ["input", "output", "config"];
  const hasFileHandling = isFileHandlingProviderResource(
    node.data.providerResource,
  );

  if (hasFileHandling) {
    defaultOpenSections.push("file");
  }

  const filesFromNodeData = node.data.files;

  // We need memo because of the weird interaction between the form, JSON object and the proxy object from Mobx.
  // It causes infinite re-renders if we don't memo.
  const files = useMemo(() => {
    if (!hasFileHandling) return {};
    const defaultFiles = getDefaultFiles(node.data.providerResource);
    return (
      (checkIfFileIsEmpty(filesFromNodeData)
        ? defaultFiles
        : filesFromNodeData) || {}
    );
  }, [filesFromNodeData, hasFileHandling, node.data.providerResource]);

  return (
    <Root
      className={accordionRootClassName}
      defaultValue={defaultOpenSections}
      type="multiple"
    >
      {hasInputMappings && (
        <AccordionItem
          className="pb-3"
          disabled={renderedInDiffView}
          title="Configure API request"
          value="input"
        >
          <InputMappings
            immutable={immutable}
            integrationResource={node.data}
            isReactive={isReactive}
            runFieldErrors={runFieldErrors}
            updateInputMapping={updateMappingInput}
          />
        </AccordionItem>
      )}
      <AccordionItem
        className="pb-1"
        disabled={renderedInDiffView}
        title="Configure API response"
        value="output"
      >
        <OutputMappings
          immutable={immutable}
          integrationResource={node.data}
          isReactive={isReactive}
          runFieldErrors={runFieldErrors}
          updateMappingOutput={updateMappingOutput}
        />
      </AccordionItem>
      {hasFileHandling && (
        <AccordionItem
          className="pb-1"
          disabled={renderedInDiffView}
          title="File Handling"
          value="file"
        >
          <FileOutputMappings
            files={files}
            immutable={immutable}
            isReactive={isReactive}
            runFieldErrors={runFieldErrors}
            updateMappingFiles={updateMappingFiles}
          />
        </AccordionItem>
      )}
      <AccordionItem
        className="pb-7"
        disabled={renderedInDiffView}
        title="Advanced settings"
        value="config"
      >
        <ConfigPane
          config={node.data.config}
          dataRetention={connection.data_retention}
          hasRawProviderRequestEnabled={hasRawProviderRequestEnabled}
          immutable={immutable}
          isReactive={isReactive}
          providerResource={node.data.providerResource}
          showTenantIDField={multiTenantConnection}
          updateConfig={updateConfig}
        />
      </AccordionItem>
    </Root>
  );
};
